import { when } from 'mobx';
import type { Account, UserProfileForm } from '@linktivity/ars-neo';
import { isProfileComplete } from './formRules';
import { buildLocalizedPath } from './url';

/**
 * Build a localized profile-page URL that redirects back to
 * `localizedReturnPath` (already locale-prefixed) after save.
 */
export const buildProfilePathWithRedirect = (
  localizedReturnPath: string
): string => {
  return `${buildLocalizedPath('profile')}?redirect=${encodeURIComponent(localizedReturnPath)}`;
};

export interface ProfileGuardSiteStore {
  readonly siteSchema?: object;
  readonly userProfileForm?: UserProfileForm;
  readonly siteLoading?: boolean;
  readonly siteSettled?: boolean;
}

export interface ProfileGuardOptions {
  accountStore: {
    readonly accountInfo?: Account;
    getAccountInfo: () => Promise<Account>;
  };
  siteStore: ProfileGuardSiteStore;
}

const hasLoadedAccount = (account?: Account): account is Account =>
  !!account && Object.keys(account).length > 0;

// `siteLoading` starts out false before the layout effect kicks off the
// fetch, so "not loading" alone can be observed before the schema exists;
// require a populated schema as well.
export const hasLoadedSite = (site: ProfileGuardSiteStore): boolean =>
  !site.siteLoading &&
  !!site.siteSchema &&
  Object.keys(site.siteSchema).length > 0;

// The schema fetch has settled at least once (success or failure) — the
// signal that distinguishes "failed, schema left empty" from "not fetched
// yet". Settled with an empty schema means there is nothing to gate on,
// so the profile gates fail open instead of blocking forever.
export const hasSettledSite = (site: ProfileGuardSiteStore): boolean =>
  !site.siteLoading && !!site.siteSettled;

// Upper bound on waiting for the site schema so a fetch that never settles
// cannot strand the user on the login page; on timeout `when` rejects and
// the gate fails open below.
const SITE_SCHEMA_WAIT_TIMEOUT_MS = 10_000;

/**
 * Post-auth profile-completeness gate. Returns a localized profile-page URL
 * (with `redirectTo` as the return target) when the logged-in account is
 * missing fields the site config marks as required, or `redirectTo` itself
 * when navigation can proceed directly. Account fetch failures never block
 * navigation; the pre-order gate in BookConfirm remains the backstop.
 */
export const resolveProfileGuardPath = async (
  guard: ProfileGuardOptions,
  redirectTo: string
): Promise<string> => {
  // Already heading to the profile page: gating would only produce a
  // profile -> profile redirect, so skip the account lookup entirely.
  const profilePath = buildLocalizedPath('profile');
  if (redirectTo === profilePath || redirectTo.startsWith(`${profilePath}?`)) {
    return redirectTo;
  }
  try {
    // Reuse the account already in the store when present: logout resets
    // accountInfo to {} in every app layout, so a populated value always
    // belongs to the current session's user.
    const cached = guard.accountStore.accountInfo;
    const [account] = await Promise.all([
      hasLoadedAccount(cached) ? cached : guard.accountStore.getAccountInfo(),
      // Wait for the site schema: with the config still unloaded no field
      // counts as required, so the gate would pass an incomplete profile.
      // A settled-but-empty schema also releases the wait — completeness
      // then trivially passes and the guard falls through to `redirectTo`.
      when(
        () => hasLoadedSite(guard.siteStore) || hasSettledSite(guard.siteStore),
        { timeout: SITE_SCHEMA_WAIT_TIMEOUT_MS }
      )
    ]);
    if (!isProfileComplete(account, guard.siteStore.userProfileForm)) {
      return buildProfilePathWithRedirect(redirectTo);
    }
  } catch {
    // Ignore and continue to the original target.
  }
  return redirectTo;
};
